import AcceleratorDoc from "../../models/AcceleratorDoc";
import API from "../../models/API";
import { BaseField, BaseFieldData } from "../../models/extraction/BaseField";
import { DefaultValueMatcher } from "../../models/matchers/DefaultValueMatcher";
import { DlfMatcher } from "../../models/matchers/DlfMatcher";
import { AcceleratorDocMetadataStore } from "../../stores/impl/AcceleratorDocMetadataStore";

export { DlfExtractor, RunFieldMatcherReponse };

class DlfExtractor {
  matcher: DlfMatcher;
  treatNotFoundAsStale: boolean;
  constructor(matcher: DlfMatcher, treatNotFoundAsStale: boolean = true) {
    this.matcher = matcher;
    this.treatNotFoundAsStale = treatNotFoundAsStale;
  }

  async extract(
    acceleratorDoc: AcceleratorDoc,
  ): Promise<BaseField | undefined> {
    if (!this.matcher.isConfigured()) {
      // return the non extracted place holder lol !
      return new BaseField({
        transformed: false,
        validationTypes: [],
        ocrConfidence: -1,
        confidence: 0,
        fieldName: this.matcher.fieldName,
        fieldStatus: "BLANK",
        fieldValue: "",
        matcherData: this.matcher.toMatcherData(),
        pageId: "",
        spanIds: [],
      });
    }

    if (this.matcher.getDiscriminator() === "F_DEFAULT") {
      return this.extractDefault();
    }

    let response;
    switch (this.matcher.getDiscriminator()) {
      case "F_REGEX":
      case "F_FUZZY":
      case "F_KEYVALUE":
      case "F_SECTION": {
        response = await this.callExtract(acceleratorDoc);
        break;
      }
      default: {
        throw new Error(
          `Cannot extract Matcher with discriminator ${this.matcher.discriminator}`,
        );
      }
    }
    if (!response) {
      console.warn(
        `Error calling API to extract with matcher ${this.matcher.getMatcherId()}`,
      );
      return undefined;
    }
    if (
      response &&
      !(response.valid || response.extractedField?._matcherData)
    ) {
      console.warn(
        `Could not extract DLF ${this.matcher.fieldName} with Matcher ${this.matcher.getMatcherId()}`,
        response.reason,
      );
      // console.log(response!.reason);
      return undefined;
    }
    // DEBUG: this should be set by the API, otherwise reuse could not work !
    if (response?.extractedField) {
      response.extractedField.fieldName = this.matcher.fieldName;
    }
    return response?.extractedField;
  }

  private async callExtract(
    acceleratorDoc: AcceleratorDoc,
  ): Promise<RunFieldMatcherReponse | null> {
    return await API.post(
      `/v0/matcher/${acceleratorDoc.uuid}/extract`,
      {
        docType: acceleratorDoc.classifiedDocType,
      },
      this.matcher.toMatcherData(),
    )
      .then((res) => {
        if (!res.ok && res.status === 404 && this.treatNotFoundAsStale) {
          AcceleratorDocMetadataStore.markAllDocMetadataStale(
            acceleratorDoc.storeId.batchInstanceUuid,
          );
        }
        return res;
      })
      .then((value: Response) => value.json())
      .then(
        (value: RunFieldMatcherReponseData) =>
          new RunFieldMatcherReponse(value),
      )
      .catch((error: string) => {
        console.log(error);
        return null;
      });
  }

  private extractDefault(): BaseField {
    return new BaseField({
      ocrConfidence: -1,
      confidence: 100,
      fieldName: this.matcher.fieldName,
      fieldStatus: "HARDCODE",
      fieldValue: (this.matcher as DefaultValueMatcher).defaultValue,
      matcherData: this.matcher.toMatcherData(),
      pageId: "",
      spanIds: [],
      transformed: false,
      validationTypes: [],
    });
  }
}

interface RunFieldMatcherReponseData {
  valid: boolean;
  reason: string;
  extractedField: BaseFieldData;
}
class RunFieldMatcherReponse {
  valid: boolean;
  reason: string;
  extractedField: BaseField;

  constructor(data: RunFieldMatcherReponseData) {
    this.valid = data.valid;
    this.reason = data.reason;
    // TODO: populate properly
    this.extractedField = new BaseField(data.extractedField);
  }
}
